import Service from "../service";

/**
 * Analytics / Dynamic-dashboard API wrapper.
 * Uses the shared Service.makeAPICall so the auth token + interceptors apply automatically.
 * Mirrors the CRM analyticsService, adapted to HRMS endpoints and the { status, data } envelope.
 */

const AB = "/analytics-builder";
const DB = "/dashboards";

// server responds with { status, success, data } - unwrap to `data`.
const unwrap = (res) => res?.data?.data;

const call = (methodName, api_url, body) =>
  Service.makeAPICall({ methodName, api_url, body });

const analyticsApi = {
  // ---- metadata ----
  getMetadata: async () => unwrap(await call(Service.getMethod, `${AB}/metadata`)),
  getFieldOptions: async (module, field) =>
    unwrap(await call(Service.getMethod, `${AB}/metadata/${module}/options`, { field })),

  // ---- components ----
  listComponents: async (type) =>
    unwrap(await call(Service.getMethod, `${AB}/components`, type ? { type } : undefined)),
  getComponent: async (id) => unwrap(await call(Service.getMethod, `${AB}/components/${id}`)),
  createComponent: async (payload) => unwrap(await call(Service.postMethod, `${AB}/components`, payload)),
  updateComponent: async (id, payload) => unwrap(await call(Service.putMethod, `${AB}/components/${id}`, payload)),
  deleteComponent: async (id) => unwrap(await call(Service.deleteMethod, `${AB}/components/${id}`)),
  runComponent: async (id, period, activeFilters, scopeEmployeeId) =>
    unwrap(await call(Service.postMethod, `${AB}/components/${id}/data`, {
      ...(period ? { period } : {}),
      ...(activeFilters && activeFilters.length ? { activeFilters } : {}),
      ...(scopeEmployeeId ? { scopeEmployeeId } : {}),
    })),
  previewComponent: async (config) => unwrap(await call(Service.postMethod, `${AB}/preview`, config)),

  // ---- dashboards ----
  listDashboards: async () => unwrap(await call(Service.getMethod, `${DB}/`)),
  getDashboard: async (id) => unwrap(await call(Service.getMethod, `${DB}/${id}`)),
  createDashboard: async (payload) => unwrap(await call(Service.postMethod, `${DB}/`, payload)),
  updateDashboard: async (id, payload) => unwrap(await call(Service.putMethod, `${DB}/${id}`, payload)),
  deleteDashboard: async (id) => unwrap(await call(Service.deleteMethod, `${DB}/${id}`)),
  saveLayout: async (id, layout) => unwrap(await call(Service.putMethod, `${DB}/${id}/layout`, { layout })),
  saveAssignment: async (id, payload) => unwrap(await call(Service.putMethod, `${DB}/${id}/assignment`, payload)),
  toggleFavorite: async (id, favorite) => unwrap(await call(Service.putMethod, `${DB}/${id}/favorite`, { favorite })),
  setDefaultDashboard: async (id) => unwrap(await call(Service.putMethod, `${DB}/${id}/default`, {})),
  getDashboardData: async (id, period, scopeEmployeeId) =>
    unwrap(await call(Service.postMethod, `${DB}/${id}/data`, {
      ...(period ? { period } : {}),
      ...(scopeEmployeeId ? { scopeEmployeeId } : {}),
    })),

  // ---- HRMS sharing / scope pickers (existing endpoints) ----
  // Employees for the sharing lists, comparator "employees" mode, and "view as employee".
  getEmployees: async () => {
    try {
      // Scope-aware list: returns exactly the employees the viewer may "view as" - the whole
      // company for Admin/HR, a manager's team for a manager, self for a regular employee.
      const res = await call(Service.getMethod, `${AB}/scope-employees`);
      const list = res?.data?.data?.employees || [];
      return list.map((e) => ({ _id: String(e._id), name: e.name, img: null }));
    } catch {
      return [];
    }
  },
  getRoles: async () => {
    try {
      const res = await call(Service.getMethod, Service.getRoles);
      const list = res?.data?.data || res?.data?.roles || [];
      return list.map((r) => ({ roleName: r.role_name || r.roleName, _id: String(r._id || r.role_name) }));
    } catch {
      return [];
    }
  },
};

export default analyticsApi;
